import ButtonText from "@component/common/ButtonText";
import ModalBox from "@component/common/ModalBox";
import ModalComponent from "@component/common/ModalComponent";
import { ModalTitle } from "@component/common/ModalText";
import RowBox from "@component/common/RowBox";
import StandardInput from "@component/common/StandardInput";
import StandardTextArea from "@component/common/StandardTextArea";
import { postModal, postOneData } from "@store/atom/post";
import { useRecoilState, useSetRecoilState } from "recoil";
import ImageInput from "@component/common/ImageInput";
import useImageInput from "@hook/useImageInput";
import { StandardImage } from "@component/common/StandardImage";
import { SelectChangeEvent } from "@mui/material";
import Dropdown from "@component/common/Dropdown";
import { CommunityData, CommunityType } from "@entity/community.entity";
import { useDictInput } from "@hook/useDictInput";
import communityRepository from "@api/community/community.repository";
import notificationRepository from "@api/notification/notification.repository";
import { useState } from "react";

export default function PostModal({ title }: { title: string }) {
  const { images, fileInput, onFileUpload, handleFileChange, setImages } = useImageInput();
  const [post, setPost] = useRecoilState<CommunityData | null>(postOneData);
  const setPostModal = useSetRecoilState(postModal);
  const [deleteImages, setDeleteImages] = useState<string[]>([]);

  const [data, setData, onChangeData] = useDictInput({
    id: post ? post.id : "",
    username: post ? post.username : "",
    title: post ? post.title : "",
    content: post ? post.content : "",
    communityType: post ? post.communityType : "",
    likeCount: post ? post.likeCount : 0,
    scrapCount: post ? post.scrapCount : 0,
    pictures: post ? post.pictures : [],
    urlAndKeys: post ? post.urlAndKeys : [],
    createdDate: post ? post.createdDate : "",
  });

  const onCanceled = () => {
    setPostModal(false);
    setPost(null);
  };

  const onSaved = async () => {
    if (
      data.communityType.length > 0 &&
      data.title.length > 0 &&
      data.content.length > 0
    ) {
      try {
        // 1. Create or update community first
        let communityId: string;
        if (!post) {
          const response = await communityRepository.createCommunity({
            ...data,
            communityType: data.communityType as CommunityType,
          });
          communityId = response.id;
          if (data.communityType === CommunityType.NOTICE) {
            await notificationRepository.sendNotificationToAll("공지가 등록되었습니다. 공지 내용을 확인해주세요.");
          }
        } else {
          await communityRepository.updateCommunityById(data.id, {
            ...data,
            communityType: data.communityType as CommunityType,
          });
          communityId = data.id;

          // 삭제할 이미지가 있다면 deletePicture API 호출
          if (deleteImages.length > 0) {
            await Promise.all(
              deleteImages.map(async (pictureId) => {
                await communityRepository.deletePicture(communityId, pictureId);
              })
            );
          }
        }

        // 2. Handle image uploads if there are any new images
        if (images.length > 0) {
          const uploadedKeys = [];
          
          // Get presigned URL and upload each image
          for (const image of images) {
            // Get presigned URL
            const presignedData = await communityRepository.getPresignedUrl(
              communityId,
              image.name
            );

            // Upload to presigned URL
            await fetch(presignedData.url, {
              method: "PUT",
              body: image,
              headers: {
                "Content-Type": image.type,
              },
            });
            
            // Collect the keys for registration
            uploadedKeys.push(presignedData.key);
          }

          // Register all uploaded images
          if (uploadedKeys.length > 0) {
            await communityRepository.createPicture(communityId, uploadedKeys);
          }
        }

        setPostModal(false);
        setPost(null);
      } catch (error) {
        console.error('Failed to save post:', error);
        // Handle error appropriately
      }
    }
  };

  // 게시판 타입과 이름 매핑
  const getCommunityTypeLabel = (type: string): string => {
    const typeMap: Record<string, string> = {
      [CommunityType.NOTICE]: "공지",
      [CommunityType.NUTRIENT_STORY]: "영양스토리",
      [CommunityType.SUCCESS_STORY]: "도전인생샷",
      [CommunityType.BUSINESS_STORY]: "비즈니스스토리",
      [CommunityType.QNA]: "Q&A",
    };
    return typeMap[type] || type;
  };

  // 요구사항에 맞는 게시판만 필터링 (순서: 공지, 영양스토리, 도전인생샷, 비즈니스스토리, Q&A)
  const allowedCommunityTypes = [
    CommunityType.NOTICE,
    CommunityType.NUTRIENT_STORY,
    CommunityType.SUCCESS_STORY,
    CommunityType.BUSINESS_STORY,
    CommunityType.QNA,
  ];

  // 드롭다운 옵션 생성 (Enum - 이름 형식)
  const communityTypeOptions = allowedCommunityTypes.map((type) => {
    const label = getCommunityTypeLabel(type);
    return `${type} - ${label}`;
  });

  // 현재 선택된 값을 "ENUM - 이름" 형식으로 변환
  const currentValue = data.communityType 
    ? `${data.communityType} - ${getCommunityTypeLabel(data.communityType)}`
    : "";

  const handleChange = (event: SelectChangeEvent) => {
    // "ENUM - 이름" 형식에서 ENUM만 추출
    const selectedEnum = event.target.value.split(" - ")[0];
    setData({
      ...data,
      communityType: selectedEnum,
    });
  };
  return (
    <>
      <ModalBox width={480}>
        <ModalTitle>{post ? title : "글 생성하기"}</ModalTitle>
        <ModalComponent
          marginBottom={32}
          description="게시판명"
          component={
            <Dropdown
              key={data.communityType}
              options={communityTypeOptions}
              value={currentValue}
              onChange={handleChange}
            />
          }
        />
        <ModalComponent
          marginBottom={32}
          description="제목"
          component={
            <StandardInput
              name="title"
              value={data.title}
              onChange={onChangeData}
            />
          }
        />
        <ModalComponent
          marginBottom={32}
          description="내용"
          component={
            <StandardTextArea
              name="content"
              value={data.content}
              onChange={(e) => {
                setData({
                  ...data,
                  content: e.target.value,
                });
              }}
              height={120}
            />
          }
        />
        <ModalComponent
          marginBottom={32}
          description="이미지"
          component={
            <RowBox justifyContent="flex-start">
              {data.urlAndKeys?.map((urlAndKey, index) => {
                return (
                  <div
                    style={{
                      width: "150px",
                      height: "150px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <button
                      onClick={() => {
                        // 삭제할 이미지 key 저장
                        setDeleteImages([...deleteImages, urlAndKey.id]);
                        setData({
                          ...data,
                          urlAndKeys: data.urlAndKeys.filter((_, i) => i !== index)
                        });
                      }}
                      style={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        backgroundColor: "white",
                        width: "50px",
                        height: "30px",
                        borderRadius: "8px",
                      }}
                    >
                      삭제
                    </button>
                    <StandardImage key={index} src={urlAndKey.url} alt="image" />
                  </div>
                );
              })}
              {images.map((image, index) => {
                const url = URL.createObjectURL(image);
                return (
                  <div
                    style={{
                      width: "150px",
                      height: "150px",
                      marginLeft: "16px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <button
                      onClick={() => {
                        setImages(images.filter((_, i) => i !== index));
                      }}
                      style={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        backgroundColor: "white",
                        width: "50px",
                        height: "30px",
                        borderRadius: "8px",
                      }}
                    >
                      삭제
                    </button>
                    <StandardImage key={index} src={url} alt="image" />
                  </div>
                );
              })}
              <ImageInput
                marginLeft={
                  data.urlAndKeys?.length > 0 || images.length > 0 ? 16 : 0
                }
                fileInput={fileInput}
                onFileUpload={onFileUpload}
                handleChange={handleFileChange}
              />
            </RowBox>
          }
        />
        <RowBox height="auto">
          <ButtonText
            onClick={onCanceled}
            color="#8D8D8D"
            backgroundColor="#F5F5F5"
          >
            취소
          </ButtonText>
          <ButtonText
            onClick={onSaved}
            color="#FFFFFF"
            backgroundColor="#84C64A"
          >
            저장
          </ButtonText>
        </RowBox>
      </ModalBox>
    </>
  );
}
